import {useEffect, useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {Dropdown} from "primereact/dropdown";
import {Divider} from "primereact/divider";
import {Tag} from "primereact/tag";
import {ConfirmDialog, confirmDialog} from "primereact/confirmdialog";
import Swal from "sweetalert2";
import L from "leaflet";
import {Circle, Marker, Polygon} from "react-leaflet";
import Map from "../../../components/molecules/Map/Map";
import {
    addProveedorTaller,
    deleteProveedorTaller,
    getCiudadesPorEstado,
    getEstados,
    getGeofencesTaller,
    getProveedoresOdoo,
    getProveedoresTaller, updateGeofence, updateProveedorTaller,
} from "./Integracion";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";

const DEFAULT_CENTER = [20.6597, -103.3496];
const ROW_HEIGHT = 46;

const INITIAL_FORM = {
    nombre: "", domicilio: "", colonia: "", cp: null,
    id_b_estado: null, id_co_ciudad: null, id_proveedor: null,
    latitud: String(DEFAULT_CENTER[0]),
    longitud: String(DEFAULT_CENTER[1]),
    geocerca:       [],
    geocercaNombre: "",
    geocercaTipo:   "poligono",
    geocercaRadio:  60,
    geocercaId:     null,  // <-- necesario
};


const vertexIcon = L.divIcon({
    className: "",
    html: `<div style="
        width:12px;height:12px;
        background:white;
        border:2.5px solid #f59e0b;
        border-radius:50%;
        cursor:grab;
        box-shadow:0 1px 4px rgba(0,0,0,.3)
    "></div>`,
    iconSize:   [12, 12],
    iconAnchor: [6, 6],
});

const rowToForm = (row, estados, proveedores, ciudadesCache) => {
    const estadoObj = estados.find((e) => e.code === row.id_b_estado) ?? null;
    const ciudades  = estadoObj ? (ciudadesCache[estadoObj.code] ?? []) : [];

    return {
        ID:             row.id,
        nombre:         row.nombre    ?? "",
        domicilio:      row.domicilio ?? "",
        colonia:        row.colonia   ?? "",
        cp:             row.cp        ?? null,
        latitud:        row.latitud   ?? String(DEFAULT_CENTER[0]),
        longitud:       row.longitud  ?? String(DEFAULT_CENTER[1]),
        id_b_estado:    estadoObj,
        id_proveedor:   proveedores.find((p) => p.code === row.id_proveedor) ?? null,
        id_co_ciudad:   ciudades.find((c) => c.code === row.id_co_ciudad) ?? { code: row.id_co_ciudad },
        geocercaId:     row.geocercaId ?? null,
        geocercaNombre: `${row.id}-${row.nombre}`,
        // estos 3 los pone openModal desde zonesCache
        geocercaTipo:   "poligono",
        geocercaRadio:  60,
        geocerca:       [],
    };
};

// ─── Modal ────────────────────────────────────────────────────────────────────

function SucursalModal({ visible, onHide, mode, formData, onChange, onSave, mapKey, estados, proveedores, ciudadesCache, onCiudadesLoaded }) {
    const readOnly = mode === "view";
    const [ciudades, setCiudades]               = useState([]);
    const [loadingCiudades, setLoadingCiudades] = useState(false);
    const [geocercaMode, setGeocercaMode]       = useState(false);

    useEffect(() => {
        setGeocercaMode(false);
    }, [visible]);

    useEffect(() => {
        if (!visible) return;  // no hacer nada si está cerrado
        if (!formData.id_b_estado) { setCiudades([]); return; }

        const code   = formData.id_b_estado.code;
        const cached = ciudadesCache[code];

        if (cached) {
            setCiudades(cached);
            const ciudad = formData.id_co_ciudad;
            if (ciudad?.code && !ciudad?.name) {
                const match = cached.find((c) => c.code === ciudad.code);
                if (match) onChange({ ...formData, id_co_ciudad: match });
            }
            return;
        }

        setLoadingCiudades(true);
        getCiudadesPorEstado(code)
            .then((data) => {
                const list = data || [];
                setCiudades(list);
                onCiudadesLoaded(code, list);
                const ciudad = formData.id_co_ciudad;
                if (ciudad?.code && !ciudad?.name) {
                    const match = list.find((c) => c.code === ciudad.code);
                    if (match) onChange({ ...formData, id_co_ciudad: match });
                }
            })
            .finally(() => setLoadingCiudades(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.id_b_estado, visible]); // <-- visible aquí es la clave

    const set = (field, value) => onChange({ ...formData, [field]: value });

    const handleEstado = (val) =>
        onChange({ ...formData, id_b_estado: val, id_co_ciudad: null });

    const handleMapClick = (e) => {
        const { lat, lng } = e.latlng;
        if (geocercaMode) {
            const nuevosPuntos = [
                ...formData.geocerca,
                { x: parseFloat(lng.toFixed(6)), y: parseFloat(lat.toFixed(6)), r: 0 },
            ];
            set("geocerca", nuevosPuntos);
        } else {
            onChange({ ...formData, latitud: lat.toFixed(6), longitud: lng.toFixed(6) });
        }
    };

    const handleMarkerDrag = (e) => {
        const { lat, lng } = e.target.getLatLng();
        onChange({ ...formData, latitud: lat.toFixed(6), longitud: lng.toFixed(6) });
    };

    const deshacerUltimoPunto = () => set("geocerca", formData.geocerca.slice(0, -1));
    const limpiarGeocerca     = () => onChange({ ...formData, geocerca: [], geocercaNombre: "" });

    const lat = parseFloat(formData.latitud) || DEFAULT_CENTER[0];
    const lng = parseFloat(formData.longitud) || DEFAULT_CENTER[1];
    const polygonPositions = formData.geocerca.map((p) => [p.y, p.x]);

    const header =
        mode === "add"  ? "Nueva sucursal" :
            mode === "edit" ? "Editar sucursal" :
                "Detalle de sucursal";

    const footer = !readOnly && (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancelar" outlined severity="secondary" onClick={onHide} />
            <Button label="Guardar"  icon="pi pi-save"              onClick={onSave} />
        </div>
    );

    return (
        <Dialog
            header={header}
            visible={visible}
            onHide={onHide}
            footer={footer}
            style={{ width: "90vw", maxWidth: "1100px" }}
            modal
            draggable={false}
        >
            <div className="grid mt-1">

                {/* ── Formulario ── */}
                <div className="col-12 md:col-5" style={{ overflowY: "auto", maxHeight: "540px" }}>

                    <div className="field">
                        <label className="block text-sm mb-1">Nombre</label>
                        <InputText
                            value={formData.nombre}
                            onChange={(e) => set("nombre", e.target.value)}
                            disabled={readOnly}
                            className="w-full"
                        />
                    </div>

                    <div className="field">
                        <label className="block text-sm mb-1">Domicilio</label>
                        <InputText
                            value={formData.domicilio}
                            onChange={(e) => set("domicilio", e.target.value)}
                            disabled={readOnly}
                            className="w-full"
                        />
                    </div>

                    <div className="formgrid grid">
                        <div className="field col">
                            <label className="block text-sm mb-1">Colonia</label>
                            <InputText
                                value={formData.colonia}
                                onChange={(e) => set("colonia", e.target.value)}
                                disabled={readOnly}
                                className="w-full"
                            />
                        </div>
                        <div className="field col">
                            <label className="block text-sm mb-1">C.P.</label>
                            <InputNumber
                                value={formData.cp}
                                onValueChange={(e) => set("cp", e.value)}
                                disabled={readOnly}
                                useGrouping={false}
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div className="field">
                        <label className="block text-sm mb-1">Estado</label>
                        <Dropdown
                            value={formData.id_b_estado}
                            options={estados}
                            onChange={(e) => handleEstado(e.value)}
                            optionLabel="name"
                            placeholder="Seleccionar..."
                            disabled={readOnly}
                            virtualScrollerOptions={{ itemSize: 38 }}
                            filter
                            className="w-full"
                        />
                    </div>

                    <div className="field">
                        <label className="block text-sm mb-1">Ciudad</label>
                        <Dropdown
                            value={formData.id_co_ciudad}
                            options={ciudades}
                            onChange={(e) => set("id_co_ciudad", e.value)}
                            optionLabel="name"
                            placeholder={
                                loadingCiudades      ? "Cargando..." :
                                    formData.id_b_estado ? "Seleccionar..." :
                                        "Selecciona estado primero"
                            }
                            disabled={readOnly || !formData.id_b_estado || loadingCiudades}
                            filter
                            className="w-full"
                        />
                    </div>

                    <div className="field">
                        <label className="block text-sm mb-1">Proveedor Odoo</label>
                        <Dropdown
                            value={formData.id_proveedor}
                            options={proveedores}
                            onChange={(e) => set("id_proveedor", e.value)}
                            optionLabel="name"
                            placeholder="Seleccionar..."
                            disabled={readOnly}
                            virtualScrollerOptions={{ itemSize: 38 }}
                            filter
                            className="w-full"
                        />
                    </div>

                    <div className="formgrid grid">
                        <div className="field col">
                            <label className="block text-sm mb-1">Latitud</label>
                            <InputText
                                value={formData.latitud}
                                onChange={(e) => set("latitud", e.target.value)}
                                disabled={readOnly}
                                className="w-full"
                            />
                        </div>
                        <div className="field col">
                            <label className="block text-sm mb-1">Longitud</label>
                            <InputText
                                value={formData.longitud}
                                onChange={(e) => set("longitud", e.target.value)}
                                disabled={readOnly}
                                className="w-full"
                            />
                        </div>
                    </div>

                    {/* ── Sección geocerca ── */}
                    {!readOnly && (
                        <>
                            <Divider />

                            <p className="text-xs font-medium uppercase m-0 mb-3"
                               style={{ letterSpacing: ".07em", color: "var(--text-color-secondary)" }}>
                                Geocerca
                            </p>

                            {/* Selector de tipo */}
                            <div className="flex gap-2 mb-3">
                                <Button
                                    label="Circular"
                                    tooltip="Geocerca circular a partir de coordenadas"
                                    icon="pi pi-circle"
                                    size="small"
                                    severity={formData.geocercaTipo === "circular" ? "warning" : "secondary"}
                                    outlined={formData.geocercaTipo !== "circular"}
                                    onClick={() => onChange({ ...formData, geocercaTipo: "circular", geocerca: [] })}
                                />
                                <Button
                                    label="Libre"
                                    tooltip="Dibuje libremente la geocerca"
                                    icon="pi pi-map"
                                    size="small"
                                    severity={formData.geocercaTipo === "poligono" ? "warning" : "secondary"}
                                    outlined={formData.geocercaTipo !== "poligono"}
                                    onClick={() => onChange({ ...formData, geocercaTipo: "poligono" })}
                                />
                            </div>

                            {/* Radio — solo circular */}
                            {formData.geocercaTipo === "circular" && (
                                <div className="field">
                                    <label className="block text-sm mb-1">Radio (metros)</label>
                                    <InputNumber
                                        value={formData.geocercaRadio}
                                        onValueChange={(e) => set("geocercaRadio", e.value ?? 60)}
                                        min={10} max={5000}
                                        suffix=" m"
                                        className="w-full"
                                    />
                                </div>
                            )}

                            {/* Controles modo mapa — solo polígono */}
                            {formData.geocercaTipo === "poligono" && (
                                <>
                                    <div className="flex gap-2 mb-3">
                                        <Button
                                            label="Mover posición"
                                            icon="pi pi-map-marker"
                                            size="small"
                                            severity={!geocercaMode ? "info" : "secondary"}
                                            outlined={geocercaMode}
                                            onClick={() => setGeocercaMode(false)}
                                        />
                                        <Button
                                            label="Dibujar geocerca"
                                            icon="pi pi-pencil"
                                            size="small"
                                            severity={geocercaMode ? "warning" : "secondary"}
                                            outlined={!geocercaMode}
                                            onClick={() => setGeocercaMode(true)}
                                        />

                                    </div>

                                    <div className="flex align-items-center justify-content-between mb-2">
                    <span className="text-sm" style={{ color: "var(--text-color-secondary)" }}>
                        {formData.geocerca.length === 0
                            ? "Sin puntos — haz clic en el mapa"
                            : `${formData.geocerca.length} punto${formData.geocerca.length !== 1 ? "s" : ""} agregado${formData.geocerca.length !== 1 ? "s" : ""}`
                        }
                        {formData.geocerca.length >= 3 && " ✓ polígono válido"}
                    </span>
                                        <div className="flex gap-1">
                                            <Button
                                                icon="pi pi-undo"
                                                rounded text severity="secondary" size="small"
                                                tooltip="Deshacer último punto"
                                                disabled={formData.geocerca.length === 0}
                                                onClick={deshacerUltimoPunto}
                                            />
                                            <Button
                                                icon="pi pi-trash"
                                                rounded text severity="danger" size="small"
                                                tooltip="Limpiar geocerca"
                                                disabled={formData.geocerca.length === 0}
                                                onClick={limpiarGeocerca}
                                            />
                                        </div>
                                    </div>

                                    <p className="text-xs m-0" style={{ color: "var(--text-color-secondary)" }}>
                                        <i className={`pi ${geocercaMode ? "pi-pencil" : "pi-map-marker"} mr-1`} />
                                        {geocercaMode
                                            ? "Modo dibujo — clic agrega vértice, arrastra para mover"
                                            : "Modo posición — clic o arrastra el marcador"
                                        }
                                    </p>

                                    {formData.geocerca.length < 3 && (
                                        <p className="text-xs m-0 mt-2" style={{ color: "var(--red-500)" }}>
                                            <i className="pi pi-exclamation-triangle mr-1" />
                                            Se necesitan al menos 3 puntos; si guardas así no se generará la geocerca.
                                        </p>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {readOnly && formData.geocerca.length > 0 && (
                        <>
                            <Divider />
                            <p className="text-xs font-medium uppercase m-0 mb-2"
                               style={{ letterSpacing: ".07em", color: "var(--text-color-secondary)" }}>
                                Geocerca — {formData.geocerca.length} puntos
                            </p>
                        </>
                    )}
                </div>

                {/* ── Mapa ── */}
                <div className="col-12 md:col-7" style={{ position: "relative" }}>

                    {!readOnly && (
                        <div style={{
                            position: "absolute", top: "12px", right: "20px", zIndex: 1000,
                            padding: "4px 10px", borderRadius: "20px", fontSize: "11px",
                            fontWeight: 500, letterSpacing: ".04em",
                            background: geocercaMode ? "#f59e0b" : "#3b82f6",
                            color: "#fff",
                        }}>
                            <i className={`pi ${geocercaMode ? "pi-pencil" : "pi-map-marker"} mr-1`} style={{ fontSize: "10px" }} />
                            {geocercaMode ? "Dibujando geocerca" : "Moviendo posición"}
                        </div>
                    )}

                    <Map
                        key={mapKey}
                        center={[lat, lng]}
                        zoom={15}
                        scrollWheelZoom
                        style={{ height: "540px", width: "100%", borderRadius: "8px" }}
                        onClick={!readOnly ? handleMapClick : undefined}
                    >
                        {/* Marcador principal de posición */}
                        <Marker
                            position={[lat, lng]}
                            draggable={!readOnly && (formData.geocercaTipo === "circular" || !geocercaMode)}
                            eventHandlers={
                                readOnly ? {} : {
                                    dragend: handleMarkerDrag,
                                }
                            }
                        />

                        {/* ── Geocerca circular ── */}
                        {formData.geocercaTipo === "circular" && (
                            <Circle
                                center={[lat, lng]}
                                radius={formData.geocercaRadio ?? 60}
                                pathOptions={{
                                    color: "#f59e0b",
                                    fillColor: "#f59e0b",
                                    fillOpacity: 0.15,
                                    weight: 2,
                                }}
                            />
                        )}

                        {/* ── Geocerca polígono ── */}
                        {formData.geocercaTipo === "poligono" && (
                            <>
                                {polygonPositions.length >= 2 && (
                                    <Polygon
                                        positions={polygonPositions}
                                        pathOptions={{
                                            color: "#f59e0b",
                                            fillColor: "#f59e0b",
                                            fillOpacity: 0.2,
                                            weight: 2,
                                            dashArray: polygonPositions.length < 3 ? "6, 6" : undefined,
                                        }}
                                    />
                                )}

                                {/* Vértices arrastrables */}
                                {formData.geocerca.map((punto, i) => (
                                    <Marker
                                        key={i}
                                        position={[punto.y, punto.x]}
                                        icon={vertexIcon}
                                        draggable={!readOnly}
                                        eventHandlers={{
                                            dragend: (e) => {
                                                const { lat: newLat, lng: newLng } = e.target.getLatLng();
                                                const updated = formData.geocerca.map((p, idx) =>
                                                    idx === i
                                                        ? { x: parseFloat(newLng.toFixed(6)), y: parseFloat(newLat.toFixed(6)), r: 0 }
                                                        : p
                                                );
                                                set("geocerca", updated);
                                            },
                                        }}
                                    />
                                ))}
                            </>
                        )}
                    </Map>
                </div>

            </div>
        </Dialog>
    );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function SucursalesPage() {
    const [rows, setRows] = useState([]);
    const [estados, setEstados] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [ciudadesCache, setCiudadesCache] = useState({});  // { [estadoCode]: Ciudad[] }
    const [zonesCache, setZonesCache] = useState({});  // { [geocercaId]: zone }
    const [zonesCargadas, setZonesCargadas] = useState(false);  // false si Wialon no respondió
    const [loading, setLoading] = useState(false);
    const [mapKey, setMapKey] = useState(0);
    const [globalFilter, setGlobalFilter] = useState("");  // <-- nuevo
    const [modal, setModal] = useState({visible: false, mode: "add", formData: INITIAL_FORM});

    useEffect(() => {
        cargarTabla();
        getEstados().then((data) => setEstados(data || []));
        getProveedoresOdoo().then((data) => setProveedores(data || []));
    }, []);

    const cargarTabla = async () => {
        setLoading(true);
        const [data, zones] = await Promise.all([
            getProveedoresTaller(),
            getGeofencesTaller(),
        ]);
        setRows(data || []);
        const map = Object.fromEntries((zones || []).map((z) => [z.id, z]));
        setZonesCache(map);
        setZonesCargadas(Array.isArray(zones));
        setLoading(false);
    };

    // "sin"       → la sucursal no tiene geocerca registrada
    // "huerfana"  → tiene ID_GEOCERCA pero la geocerca ya no existe en Wialon
    // "ok"        → tiene geocerca (o no se pudo consultar Wialon)
    const estadoGeocerca = (row) => {
        if (!row.geocercaId) return "sin";
        if (zonesCargadas && !zonesCache[Number(row.geocercaId)]) return "huerfana";
        return "ok";
    };

    const sinGeocerca = rows.filter((r) => estadoGeocerca(r) !== "ok").length;

    const handleDesactivar = (row) => {
        confirmDialog({
            message: `¿Seguro que deseas desactivar la sucursal "${row.nombre}"? Dejará de aparecer en la lista.`,
            header: "Confirmar desactivación",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            acceptLabel: "Sí, desactivar",
            rejectLabel: "No",
            accept: async () => {
                try {
                    await deleteProveedorTaller(row.id);
                    await Swal.fire("Éxito", `Sucursal "${row.nombre}" desactivada`, "success");
                    cargarTabla();
                } catch (error) {
                    await Swal.fire("Error al desactivar la sucursal", "Intenta nuevamente más tarde.", "error");
                }
            },
        });
    };

    const openModal = (mode, row = null) => {
        setMapKey((k) => k + 1);

        let formData = row
            ? rowToForm(row, estados, proveedores, ciudadesCache)
            : { ...INITIAL_FORM };

        if (mode !== "add" && row?.geocercaId) {
            const zone = zonesCache[Number(row.geocercaId)];
            if (zone) {
                const isCircular = zone.t === 3;
                formData = {
                    ...formData,
                    geocercaNombre: zone.n,
                    geocercaTipo:   isCircular ? "circular" : "poligono",
                    geocercaRadio:  isCircular ? zone.w : 60,
                    geocerca:       isCircular ? [] : zone.p,
                    latitud:        isCircular ? String(zone.p[0].y) : formData.latitud,
                    longitud:       isCircular ? String(zone.p[0].x) : formData.longitud,
                };
            }
        }

        setModal({ visible: true, mode, formData });
    };

    const closeModal = () => setModal((m) => ({ ...m, visible: false }));

    const handleChange = (updated) => setModal((m) => ({ ...m, formData: updated }));

    const handleCiudadesLoaded = (code, data) =>
        setCiudadesCache((prev) => ({ ...prev, [code]: data }));

    const handleSave = async () => {
        const { formData, mode } = modal;

        const payload = {
            ID_PROVEEDOR: formData.id_proveedor?.code,
            NOMBRE:       formData.nombre,
            DOMICILIO:    formData.domicilio,
            COLONIA:      formData.colonia,
            CP:           formData.cp,
            ID_CO_CIUDAD: formData.id_co_ciudad?.code,
            ID_B_ESTADO:  formData.id_b_estado?.code,
            LATITUD:      formData.latitud,
            LONGITUD:     formData.longitud,
        };

        const isEdit = mode === "edit";

        if (isEdit) {
            await updateProveedorTaller(formData.ID, payload);

            const tieneGeocerca = !!formData.geocercaId;
            const callMode      = tieneGeocerca ? "update" : "create";
            const geocercaId    = tieneGeocerca ? parseInt(formData.geocercaId, 10) : 0;
            const geocercaNombre = formData.geocercaNombre || `${formData.ID}-${formData.nombre}`;

            const geocercaPayload = buildGeocercaPayload(formData, callMode, geocercaId, geocercaNombre);
            if (geocercaPayload) await updateGeofence(geocercaPayload);

        } else {
            const nuevoId = await addProveedorTaller(payload);
            const geocercaNombre = `${nuevoId}-${formData.nombre}`;
            const geocercaPayload = buildGeocercaPayload(formData, "create", 0, geocercaNombre);
            if (geocercaPayload) await updateGeofence(geocercaPayload);
        }

        closeModal();
        cargarTabla();
    };

    const buildGeocercaPayload = (formData, callMode, geocercaId, nombre) => {
        const base = {
            id: geocercaId, callMode, n: nombre,
            d: "", f: 112, c: 2568583984, tc: 16733440, ts: 12,
            min: 0, max: 18, libId: "", path: "",
        };

        if (formData.geocercaTipo === "circular") {
            return {
                ...base, t: 3,
                w: formData.geocercaRadio ?? 60,
                p: [{ x: parseFloat(formData.longitud), y: parseFloat(formData.latitud), r: 0 }],
            };
        }
        if (formData.geocerca.length >= 3) {
            return { ...base, t: 2, w: 0, p: formData.geocerca };
        }
        return null;
    };

    const coordsBody = (row) => (
        <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
            {row.latitud}, {row.longitud}
        </span>
    );

    const geocercaBody = (row) => {
        const estado = estadoGeocerca(row);
        if (estado === "ok") {
            return <Tag value="Con geocerca" severity="success" icon="pi pi-check" />;
        }
        return (
            <Tag
                value={estado === "sin" ? "Sin geocerca" : "Geocerca no encontrada"}
                severity="danger"
                icon="pi pi-exclamation-triangle"
                title={estado === "sin"
                    ? "No se generó geocerca. Edita la sucursal y dibuja o configura la geocerca."
                    : "La geocerca registrada ya no existe en Wialon. Edita la sucursal para generarla de nuevo."}
            />
        );
    };

    const actionsBody = (row) => (
        <div className="flex gap-1">
            <Button
                icon="pi pi-eye"
                rounded text
                severity="secondary"
                tooltip="Ver"
                tooltipOptions={{ position: "top" }}
                onClick={() => openModal("view", row)}
            />
            <Button
                icon="pi pi-pencil"
                rounded text
                severity="info"
                tooltip="Editar"
                tooltipOptions={{ position: "top" }}
                onClick={() => openModal("edit", row)}
            />
            <Button
                icon="pi pi-ban"
                rounded text
                severity="danger"
                tooltip="Desactivar"
                tooltipOptions={{ position: "top" }}
                onClick={() => handleDesactivar(row)}
            />
        </div>
    );

    return (
        <div className="p-4">

            <div className="flex align-items-center justify-content-between mb-4">
                <div>
                    <h2 className="m-0 text-xl font-medium">Sucursales de proveedores</h2>
                    <p className="m-0 mt-1 text-sm" style={{ color: "var(--text-color-secondary)" }}>
                        {rows.length} registro{rows.length !== 1 ? "s" : ""}
                        {sinGeocerca > 0 && (
                            <span style={{ color: "var(--red-500)" }}>
                                {" · "}{sinGeocerca} sin geocerca
                            </span>
                        )}
                    </p>
                </div>
                <div className="flex gap-2">
                    <IconField iconPosition="left">
                        <InputIcon className="pi pi-search"> </InputIcon>
                        <InputText
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            placeholder="Buscar..."
                        />
                    </IconField>
                    <Button label="Nueva sucursal" icon="pi pi-plus" onClick={() => openModal("add")} />
                </div>
            </div>

            <DataTable
                value={rows}
                loading={loading}
                scrollable
                emptyMessage="Sin registros"
                size="small"
                scrollHeight="calc(100vh - 180px)"
                globalFilter={globalFilter}
            >
                <Column field="nombre"       header="Nombre"      style={{ minWidth: "220px" }} />
                <Column field="domicilio"    header="Domicilio"   style={{ minWidth: "200px" }} />
                <Column field="colonia"      header="Colonia"     style={{ minWidth: "140px" }} />
                <Column field="cp"           header="C.P."        style={{ minWidth: "80px" }} />
                <Column header="Coordenadas" body={coordsBody}    style={{ minWidth: "210px" }} />
                <Column header="Geocerca"    body={geocercaBody}  style={{ minWidth: "190px" }} />
                <Column header=""            body={actionsBody}   style={{ minWidth: "130px" }} frozen alignFrozen="right" />
            </DataTable>

            <ConfirmDialog />

            <SucursalModal
                visible={modal.visible}
                onHide={closeModal}
                mode={modal.mode}
                formData={modal.formData}
                onChange={handleChange}
                onSave={handleSave}
                mapKey={mapKey}
                estados={estados}
                proveedores={proveedores}
                ciudadesCache={ciudadesCache}
                onCiudadesLoaded={handleCiudadesLoaded}
            />

        </div>
    );
}